import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { supabase, Profile } from "../lib/supabase";
import { Session } from "@supabase/supabase-js";

export const ADMIN_EMAIL = "nagayama.giren0703@gmail.com";

export type UserMode = "consumer" | "provider" | "admin";

export interface AppUser {
  id: string;
  username: string;
  name: string;
  email: string;
  role: "consumer" | "provider" | "admin";
  serviceName?: string;
  plan?: "free" | "light";
}

interface ReportTarget {
  type: "video" | "kit" | "provider" | "review" | "message";
  id: string;
  title: string;
}

interface AppContextValue {
  isLoggedIn: boolean;
  currentUser: AppUser | null;
  session: Session | null;
  mode: UserMode;
  followedProviders: Set<string>;
  reportTarget: ReportTarget | null;
  isReportModalOpen: boolean;
  showTutorial: boolean;
  authLoading: boolean;
  isAdminMode: boolean;
  logout: () => Promise<void>;
  setMode: (mode: UserMode) => void;
  toggleFollow: (providerId: string) => Promise<void>;
  openReport: (target: ReportTarget) => void;
  closeReport: () => void;
  closeTutorial: () => void;
  triggerTutorial: () => void;
  forceShowTutorial: () => void;
  refreshProfile: () => Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);
const TUTORIAL_KEY = "tsugite_tutorial_done";
const MODE_KEY = "tsugite_mode";

function profileToAppUser(profile: Profile): AppUser {
  return {
    id: profile.id,
    username: profile.username,
    name: profile.name,
    email: profile.email,
    role: profile.role,
    serviceName: profile.service_name ?? undefined,
    plan: profile.plan,
  };
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [mode, setModeState] = useState<UserMode>("consumer");
  const [followedProviders, setFollowedProviders] = useState<Set<string>>(new Set());
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const isAdminMode = mode === "admin";

  const loadProfile = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();
    if (data) {
      const user = profileToAppUser(data as Profile);
      setCurrentUser(user);
      const savedMode = localStorage.getItem(MODE_KEY) as UserMode | null;
      const defaultMode: UserMode = "consumer";
      setModeState(savedMode ?? defaultMode);
    }
  }, []);

  const loadFollows = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("follows")
      .select("provider_id")
      .eq("follower_id", userId);
    if (data) {
      setFollowedProviders(new Set(data.map((f: { provider_id: string }) => f.provider_id)));
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (session?.user?.id) {
      await loadProfile(session.user.id);
    }
  }, [session, loadProfile]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) {
        Promise.all([loadProfile(s.user.id), loadFollows(s.user.id)]).finally(() =>
          setAuthLoading(false)
        );
      } else {
        setAuthLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user) {
        Promise.all([loadProfile(s.user.id), loadFollows(s.user.id)]);
      } else {
        setCurrentUser(null);
        setFollowedProviders(new Set());
        setModeState("consumer");
      }
    });

    return () => subscription.unsubscribe();
  }, [loadProfile, loadFollows]);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
    setSession(null);
    setFollowedProviders(new Set());
    setModeState("consumer");
    localStorage.removeItem(MODE_KEY);
  }, []);

  const setMode = useCallback((m: UserMode) => {
    setModeState(m);
    localStorage.setItem(MODE_KEY, m);
  }, []);

  const toggleFollow = useCallback(async (providerId: string) => {
    if (!currentUser) return;
    const isFollowing = followedProviders.has(providerId);

    if (isFollowing) {
      await supabase
        .from("follows")
        .delete()
        .eq("follower_id", currentUser.id)
        .eq("provider_id", providerId);
      setFollowedProviders((prev) => {
        const next = new Set(prev);
        next.delete(providerId);
        return next;
      });
    } else {
      await supabase
        .from("follows")
        .insert({ follower_id: currentUser.id, provider_id: providerId });
      setFollowedProviders((prev) => new Set([...prev, providerId]));
    }
  }, [currentUser, followedProviders]);

  const openReport = useCallback((target: ReportTarget) => {
    setReportTarget(target);
    setIsReportModalOpen(true);
  }, []);

  const closeReport = useCallback(() => {
    setIsReportModalOpen(false);
    setTimeout(() => setReportTarget(null), 300);
  }, []);

  const triggerTutorial = useCallback(() => {
    const done = localStorage.getItem(TUTORIAL_KEY);
    if (!done) setShowTutorial(true);
  }, []);

  const forceShowTutorial = useCallback(() => {
    setShowTutorial(true);
  }, []);

  const closeTutorial = useCallback(() => {
    setShowTutorial(false);
    localStorage.setItem(TUTORIAL_KEY, "1");
  }, []);

  // Expose tutorial function globally for Readdy Agent integration
  useEffect(() => {
    (window as unknown as Record<string, unknown>).__tsugiteShowTutorial = () => setShowTutorial(true);
    return () => {
      delete (window as unknown as Record<string, unknown>).__tsugiteShowTutorial;
    };
  }, []);

  return (
    <AppContext.Provider
      value={{
        isLoggedIn: !!session && !!currentUser,
        currentUser,
        session,
        mode,
        followedProviders,
        reportTarget,
        isReportModalOpen,
        showTutorial,
        authLoading,
        isAdminMode,
        logout,
        setMode,
        toggleFollow,
        openReport,
        closeReport,
        closeTutorial,
        triggerTutorial,
        forceShowTutorial,
        refreshProfile,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
